import React, { useEffect, useRef, useState } from "react";
import Navbar from "./navbar/navbar";
import { ActiveElement, Attributes } from "../../types/type";
import { handleImageUpload } from "../../lib/shapes";
import { defaultNavElement } from "../../constants";
import { handleDelete, handleKeyDown } from "../../lib/key-events";
import LeftSidebar from "./left-sidebar/left-sidebar";
import useCanvasState from "../../hooks/useCanvasState";
import RightSidebar from "./right-sidebar/right-sidebar";
import CanvasBoard from "./canvas-board/canvas-board";
import {
  handleCanvasMouseUp,
  handleCanvasObjectModified,
  handleCanvasObjectScaling,
  handleCanvasSelectionCreated,
  handlePathCreated,
  handleResize,
  handleCanvasMouseDown,
  initializeFabric,
  handleCanvasToJSON,
  handleCanvasSelectionCleared,
  handleCanvasSelectionUpdated,
  getDefaultElementAttributes,
  captureCanvasObjectOriginalCenter,
  recenterTextToOriginalCenter,
  getPngImageFromCanvas,
  handleCanvasObjectRotating,
  loadCanvasDataComingFromJSON,
} from "../../lib/canvas";
import { createTemplate, updateTemplate } from "../../api/admin/templates";
import TemplateSettingsDialog from "./template-settings-dialog/template-settings-dialog";
import { notify } from "../../lib/utils";
import Loader from "../common/loader/loader";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants/routes";
import { isEqual } from "lodash";

const DesignBoard = ({ templateData, isEditMode }: any) => {
  const navigate = useNavigate();

  // custom fonts loaded state and window event listener
  const [customFontsLoaded, setCustomFontsLoaded] = useState(false);

  useEffect(() => {
    // @ts-ignore
    if (window.customFontsLoaded) setCustomFontsLoaded(true);

    const customFontsLoadedListener = () => {
      setCustomFontsLoaded(true);
    };

    window.addEventListener("customFontsLoaded", customFontsLoadedListener);

    return () => {
      window.removeEventListener(
        "customFontsLoaded",
        customFontsLoadedListener
      );

      setCustomFontsLoaded(false);
    };
  }, []);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<fabric.Canvas | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const svgInputRef = useRef<HTMLInputElement>(null);
  const shapeRef = useRef<fabric.Object | null>(null);

  // history and redo refs.
  const historyRef = useRef<any[]>([]);
  const redoRef = useRef<any[]>([]);
  const isRestoringRef = useRef(false);
  const suppressHistoryRef = useRef(false); // stop history while grouping/ungrouping, to not push in middle operations.

  // canvas dimensions, merge it in template settings.
  const [templateSettings, setTemplateSettings] = useState({
    isPublished: false,

    internalName: "",

    displayName: {
      en: "",
    },

    slug: "",

    description: {
      en: "",
    },

    tags: [],

    fitCategory: {
      light: true,
      dark: true,
    },

    canvasDimensions: {
      width: 500,
      height: 500,
    },

    projectIds: [],
    categorySelections: [],
    printLocations: [],
    fetchOnlyWithinSets: false,
    sharedCustomization: false,
  });

  // template settings dialog state
  const [isTemplateSettingsOpen, setIsTemplateSettingsOpen] = useState(false);

  const [templateSettingsFormHasErrors, setTemplateSettingsFormHasErrors] =
    React.useState<any>(false);

  const handleCloseTemplateSettingsDialog = () => {
    setIsTemplateSettingsOpen(false);
    // set the select element as the active element
    setActiveElement(defaultNavElement);
  };

  // resize canvas, will be called from the template settings dialog.
  const handleResizeCanvas = (newDimensions: {
    width: number;
    height: number;
  }) => {
    if (!fabricRef.current)
      return notify({ type: "error", message: "Canvas not found!" });

    // resize the canvas
    handleResize({
      canvas: fabricRef.current,
      newDimensions,
    });

    // set the new dimensions in the template settings
    setTemplateSettings((prev) => ({
      ...prev,
      canvasDimensions: newDimensions,
    }));
  };

  // menu and navbar active item here. Not canvas objects or shapes.
  const [activeElement, setActiveElement] = useState<ActiveElement>({
    name: "",
    value: "",
    icon: "",
  });

  const [elementAttributes, setElementAttributes] = useState<Attributes>(
    getDefaultElementAttributes()
  );

  // keep all data updated in the ref to avoid stale closure issue.
  // otherwise, saving directly fails because the state is not updated.
  const saveDataRef = useRef<any>(null);

  // UseEffect to update the ref whenever templateSettings changes
  useEffect(() => {
    // keep it updated in the ref
    saveDataRef.current = {
      templateData,
      templateSettings,
      isEditMode,
    };
  }, [templateSettings, isEditMode, templateData]);

  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  // save the template
  const saveTemplate = async () => {
    if (templateSettingsFormHasErrors)
      return notify({
        type: "error",
        message:
          "Please fill all the required fields in the templates settings form!",
      });

    if (saveDataRef.current === null)
      return notify({
        type: "error",
        message: "Template is not ready to be saved!",
      });

    const { templateSettings, isEditMode, templateData } = saveDataRef.current;

    if (!fabricRef.current)
      return notify({ type: "error", message: "Canvas not found!" });

    // if canvas is empty, then notify the user.
    if (fabricRef.current.getObjects().length === 0)
      return notify({
        type: "error",
        message:
          "Canvas is empty! You need to add some shapes to save the template.",
      });

    if (!templateSettings.internalName)
      return notify({
        type: "error",
        message: "Template Internal Name is required!",
      });

    if (!templateSettings.displayName?.en)
      return notify({
        type: "error",
        message: "Display Name EN Internal Name is required!",
      });

    if (!templateSettings.slug)
      return notify({ type: "error", message: "Template slug is required!" });

    setIsSavingTemplate(true);
    // Give React time to re-render with the loader showing
    await new Promise((resolve) => setTimeout(resolve, 0));

    const canvasInJSON = handleCanvasToJSON({ fabricRef });

    // generate preview image on the client here, from the canvas.
    let canvasPngImage = getPngImageFromCanvas(fabricRef);

    // dynamic logic for deciding if we update the image or not, in edit mode.
    // based if canvas has changed or not, to prevent unnecessary updates on s3.
    if (isEditMode && templateData?._id) {
      const previousCanvasInJSON = templateData?.canvas;
      // check both are equal

      const isCanvasEqual = isEqual(previousCanvasInJSON, canvasInJSON);

      // if they are equal, then don't update the image,
      if (isCanvasEqual && templateData?.previewImage) {
        // keep the previous image as it is.
        canvasPngImage = templateData?.previewImage;
      }
    }

    // if is edit mode, then update the template.
    let response = null;

    if (isEditMode && templateData?._id) {
      response = await updateTemplate(templateData._id, {
        ...templateSettings,
        canvasDimensions: templateSettings.canvasDimensions,
        canvas: canvasInJSON,
        previewImage: canvasPngImage,
      });
    } else {
      response = await createTemplate({
        ...templateSettings,
        canvasDimensions: templateSettings.canvasDimensions,
        canvas: canvasInJSON,
        previewImage: canvasPngImage,
      });
    }

    if (
      !response ||
      (isEditMode && response.status !== 200) || // put
      (!isEditMode && response.status !== 201) // post
    ) {
      setIsSavingTemplate(false);
      console.log("axios status", response?.status);
      // check if slug duplication issue.
      if (response?.status === 409) {
        console.log("slug duplication issue");
        return notify({
          type: "error",
          message: "Template with this slug already exists!",
        });
      }

      return notify({
        type: "error",
        message: "Failed to save the template!",
      });
    }

    setIsSavingTemplate(false);
    // navigate to the template page
    navigate(ROUTES.TEMPLATES);
  };

  /**
   * isDrawing is a boolean that tells us if the user is drawing on the canvas.
   * We use this to determine if the user is drawing or not.
   */
  const isDrawing = useRef(false);

  /**
   * selectedShapeRef is a reference to the shape that the user has selected.
   * For example, if the user has selected the rectangle shape, then this will
   * be set to "rectangle".
   *
   * We're using refs here because we want to access these variables inside the
   * event listeners. We don't want to lose the values of these variables when
   * the component re-renders. Refs help us with that.
   */
  const selectedShapeRef = useRef<string | null>(null);

  /**
   * activeObjectRef is a reference to the active/selected object in the canvas
   *
   * We want to keep track of the active object so that we can keep it in
   * selected form when user is editing the width, height, color etc
   * properties/attributes of the object.
   *
   * Since we're using live storage to sync shapes across users in real-time,
   * we have to re-render the canvas when the shapes are updated.
   * Due to this re-render, the selected shape is lost. We want to keep track
   * of the selected shape so that we can keep it selected when the
   * canvas re-renders.
   */
  const activeObjectRef = useRef<fabric.Object | null>(null);

  const isEditingRef = useRef(false);

  // when navbar item is clicked
  const handleActiveElement = (elem: ActiveElement) => {
    setActiveElement(elem);

    switch (elem?.value) {
      // delete all the shapes from the canvas
      case "reset":
        // suppress history for this action
        suppressHistoryRef.current = true;
        // clear the storage
        deleteAllShapes();
        // clear the canvas
        fabricRef.current?.clear();
        // set "select" as the active element
        setActiveElement(defaultNavElement);

        // reset the suppress history
        setTimeout(() => {
          suppressHistoryRef.current = false;
          // manually push the empty state to history
          saveStateToHistory();
        }, 0);
        break;

      // delete the selected shape from the canvas
      case "delete":
        // delete it from the canvas
        handleDelete(fabricRef.current as any, deleteShapeFromStorage);
        // set "select" as the active element
        setActiveElement(defaultNavElement);
        break;

      // upload an image to the canvas
      case "image":
        // trigger the click event on the input element which opens the file dialog
        imageInputRef.current?.click();
        /**
         * set drawing mode to false
         * If the user is drawing on the canvas, we want to stop the
         * drawing mode when clicked on the image item from the dropdown.
         */
        isDrawing.current = false;

        if (fabricRef.current) {
          // disable the drawing mode of canvas
          fabricRef.current.isDrawingMode = false;
        }
        break;

      case "svg":
        // trigger the click event on the input element which opens the file dialog
        svgInputRef.current?.click();
        /**
         * set drawing mode to false
         * If the user is drawing on the canvas, we want to stop the
         * drawing mode when clicked on the image item from the dropdown.
         */
        isDrawing.current = false;

        if (fabricRef.current) {
          // disable the drawing mode of canvas
          fabricRef.current.isDrawingMode = false;
        }
        break;

      // template settings dialog
      case "settings":
        setIsTemplateSettingsOpen(true);
        // setActiveElement(defaultNavElement);
        break;

      // undo and redo actions
      case "undo":
        undo();
        setActiveElement(defaultNavElement);
        break;

      case "redo":
        redo();
        setActiveElement(defaultNavElement);
        break;

      // then it is a shape, so set the selected shape to the selected element.
      default:
        // set the selected shape to the selected element
        selectedShapeRef.current = elem?.value as string;
        break;
    }
  };

  const {
    canvasObjects,
    reorderCanvasObjects,
    syncShapeInStorage,
    resetState: deleteAllShapes,
    removeFromCanvas: deleteShapeFromStorage,
    loadCanvasDataComingFromJSONIntoState,
  } = useCanvasState(fabricRef);

  const undo = () => {
    if (!fabricRef.current) return;

    if (historyRef.current.length === 0) return; // nothing to undo

    // If only one state, undo clears canvas (empty state)
    if (historyRef.current.length === 1) {
      const currentState = historyRef.current.pop()!;
      redoRef.current.push(currentState);

      isRestoringRef.current = true;

      // if edit mode, we want to reset the template from what we have in the server.
      if (isEditMode && templateData && templateData.canvas) {
        fabricRef.current.clear();
        // load the canvas data from the template data.
        loadCanvasDataComingFromJSON(
          fabricRef.current,
          templateData.canvas,
          (canvas: any) => {
            // populate the state from the template data.
            loadCanvasDataComingFromJSONIntoState(canvas.getObjects() as any);
          }
        );
      } else {
        // clear the canvas
        fabricRef.current.clear();
        loadCanvasDataComingFromJSONIntoState([]);
      }

      setTimeout(() => {
        isRestoringRef.current = false;
      }, 0);

      return;
    }

    // Pop current state and push to redo
    const currentState = historyRef.current.pop()!;
    redoRef.current.push(currentState);

    // Load previous state
    const prevState = historyRef.current[historyRef.current.length - 1];
    if (!prevState) return;

    isRestoringRef.current = true;
    fabricRef.current.clear();
    loadCanvasDataComingFromJSON(
      fabricRef.current,
      prevState,
      (canvas: any) => {
        loadCanvasDataComingFromJSONIntoState(canvas.getObjects() as any);
      }
    );

    setTimeout(() => {
      isRestoringRef.current = false;
    }, 0);
  };

  const redo = () => {
    console.log("Redo called");
    if (!fabricRef.current || redoRef.current.length === 0) return;

    // Pop next redo state
    const nextState = redoRef.current.pop()!;
    if (!nextState) return;

    isRestoringRef.current = true;
    fabricRef.current.clear();
    loadCanvasDataComingFromJSON(
      fabricRef.current,
      nextState,
      (canvas: any) => {
        loadCanvasDataComingFromJSONIntoState(canvas.getObjects() as any);
      }
    );

    // Push redone state back to history
    historyRef.current.push(nextState);

    setTimeout(() => {
      isRestoringRef.current = false;
    }, 0);
  };

  const MAX_HISTORY = 20;

  const saveStateToHistory = () => {
    console.log("Saving state to history");
    console.log("Current canvas fabric:", fabricRef.current);
    console.log("Current history:", historyRef.current);
    if (!fabricRef.current) {
      console.warn("Fabric canvas is not initialized, skipping history save");
      return;
    }
    if (isRestoringRef.current) {
      console.log("Restoring state, skipping history save");
      return;
    }
    if (suppressHistoryRef.current) {
      console.log("Suppressing history save");
      return; // skip saving if suppressHistory is true
    }

    const json = handleCanvasToJSON({ fabricRef });
    historyRef.current.push(json);

    // Trim history if it exceeds MAX_HISTORY
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.shift(); // remove oldest state
    }

    redoRef.current = []; // clear redo on new action
  };

  const populateStateFromTemplate: any = (
    fabricLoadedCanvas: any,
    overrideSettings = true
    // initial call set the settings as well, but on undo, no.
  ) => {
    // populate the sidebars needed state. The canvas state and the settings state.
    // update template settings from the template data.

    let canvasObjects: any = fabricLoadedCanvas.getObjects();

    // if we are in edit mode, the canvas coming from the server has objects,
    // but populated canvas, has no objects, with heavy many objects takes time to load.
    // So, we need to wait for the canvas
    // to load all the objects, then populate the state again.
    // set timeout to call the function again after 50ms.
    if (
      (!canvasObjects || canvasObjects.length === 0) &&
      isEditMode &&
      templateData &&
      templateData.canvas &&
      templateData.canvas.objects
    ) {
      // canvas is not loaded yet, so wait for it to load.
      return setTimeout(() => populateStateFromTemplate(fabricRef.current), 50);
    }

    loadCanvasDataComingFromJSONIntoState(canvasObjects);

    if (!overrideSettings) return;

    setTemplateSettings((prev) => ({
      ...prev,
      internalName: templateData.internalName,
      displayName: templateData.displayName,
      isPublished: templateData.isPublished,
      slug: templateData.slug,
      description: templateData.description,
      tags: templateData.tags || [], // not saved on server yet,
      fitCategory: templateData.fitCategory,
      canvasDimensions: templateData.canvasDimensions,
      projectIds: templateData.projectIds,
      categorySelections: templateData.categorySelections,
      printLocations: templateData.printLocations,
      fetchOnlyWithinSets: templateData.fetchOnlyWithinSets,
      sharedCustomization: templateData.sharedCustomization,
    }));
  };

  const handleWindowKeyDown = (e: any) =>
    handleKeyDown({
      e,
      canvas: fabricRef.current,
      undo,
      redo,
      syncShapeInStorage,
      deleteShapeFromStorage,
    });

  // Canvas UseEffect
  useEffect(() => {
    // wait for fonts to load.
    if (!customFontsLoaded) return;

    // initialize the fabric canvas
    const canvas = initializeFabric({
      canvasRef,
      fabricRef,
      dimensions: templateSettings.canvasDimensions,
      isEditMode,
      templateData,
      suppressHistoryRef,
    });

    // if is edit mode, then populate the state from the template data,
    // canvas and settings.

    if (isEditMode && templateData) {
      populateStateFromTemplate(canvas);
    }

    /**
     * listen to the mouse down event on the canvas which is fired when the
     * user clicks on the canvas
     *
     * Event inspector: http://fabricjs.com/events
     * Event list: http://fabricjs.com/docs/fabric.Canvas.html#fire
     */
    canvas.on("mouse:down", (options) => {
      handleCanvasMouseDown({
        options,
        canvas,
        selectedShapeRef,
        isDrawing,
        shapeRef,
      });
    });

    /**
     * listen to the mouse up event on the canvas which is fired when the
     * user releases the mouse on the canvas
     *
     * Event inspector: http://fabricjs.com/events
     * Event list: http://fabricjs.com/docs/fabric.Canvas.html#fire
     */
    canvas.on("mouse:up", () => {
      handleCanvasMouseUp({
        canvas,
        isDrawing,
        shapeRef,
        activeObjectRef,
        selectedShapeRef,
        syncShapeInStorage,
        setActiveElement,
      });
    });

    /**
     * listen to the path created event on the canvas which is fired when
     * the user creates a path on the canvas using the freeform drawing
     * mode
     *
     * Event inspector: http://fabricjs.com/events
     * Event list: http://fabricjs.com/docs/fabric.Canvas.html#fire
     */
    canvas.on("path:created", (options) => {
      handlePathCreated({
        options,
        syncShapeInStorage,
      });
    });

    /**
     * listen to the object modified event on the canvas which is fired
     * when the user modifies an object on the canvas. Basically, when the
     * user changes the width, height, color etc properties/attributes of
     * the object or moves the object on the canvas.
     *
     * Event inspector: http://fabricjs.com/events
     * Event list: http://fabricjs.com/docs/fabric.Canvas.html#fire
     */
    canvas.on("object:modified", (options) => {
      saveStateToHistory();
      handleCanvasObjectModified({
        options,
        syncShapeInStorage,
      });
    });

    canvas.on("object:added", saveStateToHistory);

    canvas.on("object:removed", saveStateToHistory);

    canvas.on("text:editing:entered", (options) => {
      const textObj = options.target;

      if (!textObj || textObj?.type !== "i-text") return;

      // calculate its originalCenter, and set it on the object,
      // so when the changed event happens, we can reposition it to the original center.
      const originalCenter = captureCanvasObjectOriginalCenter(textObj);

      // let's store it on the object like, lastOriginalCenter.

      // custom property on the object.
      // @ts-ignore
      textObj["lastOriginalCenter"] = originalCenter;
    });

    // only the native text edited will be repositioned here. The input one,
    // will calculate its center and position accordingly.
    canvas.on("text:changed", (options) => {
      const textObj = options.target;

      if (!textObj || textObj?.type !== "i-text") return;

      // SKIP the lock for testing.
      // @ts-ignore
      const consumerLocks = textObj.consumerLocks as any;

      // if the update in place lock is there, and true, do re-centering.
      if (consumerLocks.updateTextInPlace === true) {
        // get the original center from the object.
        // @ts-ignore
        const originalCenter = textObj["lastOriginalCenter"];

        if (!originalCenter) return;

        // recenter the text to the original center.
        recenterTextToOriginalCenter(textObj, originalCenter, canvas);
      }
    });

    /**
     * listen to the selection created event on the canvas which is fired
     * when the user selects an object on the canvas.
     *
     * Event inspector: http://fabricjs.com/events
     * Event list: http://fabricjs.com/docs/fabric.Canvas.html#fire
     */
    canvas.on("selection:created", (options) => {
      handleCanvasSelectionCreated({
        options,
        setElementAttributes,
      });
    });

    canvas.on("selection:updated", (options) => {
      handleCanvasSelectionUpdated({
        canvas, // special case with the updated event.
        options,
        setElementAttributes,
      });
    });

    canvas.on("selection:cleared", (options) => {
      handleCanvasSelectionCleared({
        options,
        setElementAttributes,
      });
    });

    /**
     * listen to the scaling event on the canvas which is fired when the
     * user scales an object on the canvas.
     *
     * Event inspector: http://fabricjs.com/events
     * Event list: http://fabricjs.com/docs/fabric.Canvas.html#fire
     */
    canvas.on("object:scaling", (options) => {
      handleCanvasObjectScaling({
        options,
        setElementAttributes,
      });
    });

    canvas.on("object:rotating", (options) => {
      handleCanvasObjectRotating({
        options,
        setElementAttributes,
      });
    });

    /**
     * listen to the key down event on the window which is fired when the
     * user presses a key on the keyboard.
     *
     * We're using this to perform some actions like delete, copy, paste, etc when the user presses the respective keys on the keyboard.
     */
    window.addEventListener("keydown", handleWindowKeyDown);

    // dispose the canvas and remove the event listeners when the component unmounts
    return () => {
      /**
       * dispose is a method provided by Fabric that allows you to dispose
       * the canvas. It clears the canvas and removes all the event
       * listeners
       *
       * dispose: http://fabricjs.com/docs/fabric.Canvas.html#dispose
       */
      canvas.dispose();

      window.removeEventListener("keydown", handleWindowKeyDown);
    };
  }, [canvasRef, isEditMode, templateData, customFontsLoaded]);

  return (
    <main className="min-h-screen h-screen w-full bg-body overflow-hidden">
      <Navbar
        imageInputRef={imageInputRef}
        activeElement={activeElement}
        handleImageUpload={(e: any) => {
          e.stopPropagation();

          handleImageUpload({
            file: e.target.files[0],
            canvas: fabricRef as any,
            shapeRef,
            syncShapeInStorage,
          });
          e.target.value = "";
        }}
        svgInputRef={svgInputRef}
        handleSvgUpload={(e: any) => {
          e.stopPropagation();

          handleImageUpload({
            file: e.target.files[0],
            canvas: fabricRef as any,
            shapeRef,
            syncShapeInStorage,
          });

          e.target.value = "";
        }}
        handleActiveElement={handleActiveElement}
        saveTemplate={saveTemplate}
        isSavingTemplate={isSavingTemplate}
      />

      <TemplateSettingsDialog
        open={isTemplateSettingsOpen}
        onClose={handleCloseTemplateSettingsDialog}
        templateSettings={templateSettings}
        setTemplateSettings={setTemplateSettings}
        handleResizeCanvas={handleResizeCanvas}
        setFormHasErrors={setTemplateSettingsFormHasErrors}
      />

      <section className="flex w-full h-full flex-row bg-borderColor">
        <LeftSidebar
          elementAttributes={elementAttributes}
          setElementAttributes={setElementAttributes}
          fabricRef={fabricRef}
          isEditingRef={isEditingRef}
          activeObjectRef={activeObjectRef}
          syncShapeInStorage={syncShapeInStorage}
          allShapes={Array.from(canvasObjects)}
        />

        {!customFontsLoaded && <Loader pageLoader />}

        {customFontsLoaded && (
          <CanvasBoard canvasRef={canvasRef} undo={undo} redo={redo} />
        )}

        <RightSidebar
          elementAttributes={elementAttributes}
          setElementAttributes={setElementAttributes}
          fabricRef={fabricRef}
          isEditingRef={isEditingRef}
          activeObjectRef={activeObjectRef}
          syncShapeInStorage={syncShapeInStorage}
          deleteShapeFromStorage={deleteShapeFromStorage}
          reorderCanvasObjects={reorderCanvasObjects}
          saveStateToHistory={saveStateToHistory}
          suppressHistoryRef={suppressHistoryRef}
        />
      </section>
    </main>
  );
};

export default DesignBoard;
